import { useState } from 'preact/hooks';
import { useI18n } from '../i18n';
import { useAuth } from '../lib/auth';
import { deleteAccount } from '../lib/api';
import { LangSwitcher } from '../components/LangSwitcher';

/**
 * Self-serve account deletion page reachable at /:locale/account/delete.
 *
 * Reasons this page exists:
 *   - Apple App Store + Google Play Store both require a public URL
 *     where a user can delete their account without installing the app.
 *     The URL goes on the store listing. The Play form rejects URLs
 *     that sit behind auth, so the explanatory content (steps, data
 *     types deleted/kept, retention period) renders for anonymous
 *     visitors too — only the destructive button itself is gated.
 *   - GDPR / Swiss FADP right-to-be-forgotten.
 *
 * Flow: render the warning + retention info for everyone → if the
 * visitor is signed in, show the type-DELETE confirmation form → on
 * submit, POST DELETE /api/me, sign out, and show a "your account is
 * being deleted" success screen. Signed-out visitors see a "Sign in
 * to delete" CTA in place of the form.
 */
export function AccountDelete() {
  const { t, localePath } = useI18n();
  const { status, accessToken, user, signOut } = useAuth();
  const [confirmText, setConfirmText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<{ deletedAt: string; graceDays: number } | null>(null);

  const matches = confirmText.trim().toUpperCase() === 'DELETE';
  const signedIn = status === 'signed-in';
  const signedOut = status === 'signed-out';
  // Build the return-to so the user lands back here after sign-in.
  const loginReturnURL = `${localePath('/login')}?return=${encodeURIComponent(localePath('/account/delete'))}`;

  async function submit() {
    if (!matches || submitting || !accessToken) return;
    setSubmitting(true);
    setErr(null);
    try {
      const res = await deleteAccount(accessToken);
      // Clear local session state so the user sees the success screen
      // rather than getting punted back to login by the auth gate.
      await signOut();
      setDone({ deletedAt: res.deletedAt, graceDays: res.graceDays });
    } catch (e: any) {
      setErr(e?.message || 'failed');
    } finally {
      setSubmitting(false);
    }
  }

  if (status === 'unknown') {
    return (
      <main class="account-delete">
        <p class="account-delete__loading">{t('account.delete.loading')}</p>
      </main>
    );
  }

  if (done) {
    return (
      <main class="account-delete">
        <div class="account-delete__card account-delete__card--done">
          <h1>{t('account.delete.done_title')}</h1>
          <p>{t('account.delete.done_body')}</p>
          <p class="account-delete__meta">
            {t('account.delete.done_grace').replace('{n}', String(done.graceDays))}
          </p>
          <a class="account-delete__link" href={localePath('/')}>
            {t('account.delete.done_home')}
          </a>
        </div>
      </main>
    );
  }

  return (
    <main class="account-delete">
      <div class="account-delete__card">
        <div class="account-delete__top">
          <a class="account-delete__home" href={localePath('/')}>← BeerCup</a>
          <LangSwitcher theme="dark" />
        </div>
        <h1>{t('account.delete.title')}</h1>
        {user?.email && <p class="account-delete__email">{user.email}</p>}

        {/* In-app path callout — Apple/Google require an in-app delete,
            and that's the lower-friction option for users who have the
            app installed. Surfacing it first reduces accidental web
            deletions when the user really wanted the in-app flow. */}
        <aside class="account-delete__alt">
          <h2>{t('account.delete.alt_intro_title')}</h2>
          <p>{t('account.delete.alt_intro_body')}</p>
        </aside>

        <p>{t('account.delete.body1')}</p>
        <ul class="account-delete__list">
          <li>{t('account.delete.li_anonymise')}</li>
          <li>{t('account.delete.li_revoke')}</li>
          <li>{t('account.delete.li_grace')}</li>
          <li>{t('account.delete.li_predictions')}</li>
        </ul>
        <p class="account-delete__warn">{t('account.delete.warning')}</p>

        {signedIn && (
          <>
            <label class="account-delete__label" for="account-delete-confirm">
              {t('account.delete.input_label')}
            </label>
            <input
              id="account-delete-confirm"
              class="account-delete__input"
              autoComplete="off"
              spellcheck={false}
              value={confirmText}
              placeholder="DELETE"
              onInput={(e) => setConfirmText((e.currentTarget as HTMLInputElement).value)}
            />
            {err && <p class="account-delete__error">{err}</p>}
            <div class="account-delete__actions">
              <a class="account-delete__cancel" href={localePath('/')}>
                {t('account.delete.cancel')}
              </a>
              <button
                type="button"
                class="account-delete__danger"
                disabled={!matches || submitting}
                onClick={submit}
              >
                {submitting ? t('account.delete.submitting') : t('account.delete.confirm')}
              </button>
            </div>
          </>
        )}

        {signedOut && (
          <aside class="account-delete__signin">
            <h2>{t('account.delete.signin_cta_title')}</h2>
            <p>{t('account.delete.signin_cta_body')}</p>
            <a class="account-delete__signin-btn" href={loginReturnURL}>
              {t('account.delete.signin_cta_button')}
            </a>
          </aside>
        )}

        {/* Email fallback for users who can't sign in — covers the GDPR
            "user has lost access" edge case. Sits below the form so a
            successful self-serve deletion doesn't have to scroll past it. */}
        <aside class="account-delete__fallback">
          <h2>{t('account.delete.email_fallback_title')}</h2>
          <p>{t('account.delete.email_fallback_body')}</p>
          <a href="mailto:info@insert-coin.io">info@insert-coin.io</a>
        </aside>
        <nav class="account-delete__nav" aria-label="legal pages">
          <a href={localePath('/privacy')}>{t('legal.privacy_title')}</a>
          <span>·</span>
          <a href={localePath('/terms')}>{t('legal.terms_title')}</a>
          <span>·</span>
          <a href={localePath('/support')}>{t('support.title')}</a>
        </nav>
      </div>
    </main>
  );
}
